import { Component, effect, inject, input, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CsIconModule } from '@components/icon';
import { DirectivesModule } from '@directives/directives.module';
import { ExperimentService } from '@services/experiment';
import { UserService } from '@services/user/user.service';
import { EventLoggerService } from '@services/event-logger-service';
import { filter } from 'rxjs';
import { NavButtonConfig, getNavButtonConfig, NAV_ROUTES, GENDER_ICONS, NAV_ROUTE_ENTRIES } from './bottom-nav.constants';
import { AppConfig } from '../../app.config';

@Component({
  selector: 'app-bottom-nav',
  templateUrl: './bottom-nav.html',
  imports: [CsIconModule, DirectivesModule],
  host: { class: 'tw-contents' },
})
export class BottomNavComponent {
  user = input.required<Table.User>();
  isOnHoldPopupVisible = input(false);

  private readonly router = inject(Router);
  private readonly experimentService = inject(ExperimentService);
  private readonly userService = inject(UserService);
  private readonly appConfig = inject(AppConfig);
  private readonly eventLogger = inject(EventLoggerService);

  isExperimentActive = signal(false);
  navButtons = signal<NavButtonConfig[]>([]);
  activeButtonKey = signal('');
  isRegimenPage = signal(false);

  constructor() {
    effect(() => {
      const user = this.user();
      if (user) {
        this.initExperiment();
      }
    });

    this.updateActiveButtonFromUrl(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe((event) => {
      this.updateActiveButtonFromUrl(event.urlAfterRedirects || event.url);
    });
  }

  isButtonActive(btn: NavButtonConfig): boolean {
    if (btn.key === this.appConfig.Shared.NAVIGATION.HOME && this.isOnHoldPopupVisible()) return false;
    return this.activeButtonKey() === btn.key;
  }

  onButtonClick(key: string): void {
    if (this.activeButtonKey() === key) return;
    this.fireAnalytics(key);
    this.navigate(key);
  }

  private navigate(key: string): void {
    const route = NAV_ROUTES[key];
    if (!route) return;
    this.router.navigate(route.path, route.queryParams ? { queryParams: route.queryParams } : undefined);
  }

  private fireAnalytics(key: string): void {
    const { SHOP, PHOTO, HELP, EXPLORE } = this.appConfig.Shared.NAVIGATION;
    switch (key) {
      case SHOP:
        this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-shop' }));
        break;
      case PHOTO:
        this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-camera' }));
        break;
      case HELP:
        this.eventLogger.trackEvent('home-need-help-nav');
        this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'home-need-help-nav' }));
        break;
      case EXPLORE:
        this.eventLogger.cleverTapEvent('click-explore', JSON.stringify({ pageName: 'explore' }));
        break;
    }
  }

  private initExperiment(): void {
    const experiment = this.experimentService.getBottomNavConfig();
    if (!experiment?.enable || !experiment?.variant) {
      this.isExperimentActive.set(false);
      return;
    }
    this.isExperimentActive.set(true);
    const isUnpaid = this.userService.isUnpaidUser(this.user());
    const buttonKeys: string[] = isUnpaid ? experiment.variant.acq : experiment.variant.ret;
    const gender: string = this.user()?.get('Gender') || this.appConfig.Shared.Gender.FEMALE;
    this.navButtons.set(
      (buttonKeys || [])
        .filter((key: string) => !(isUnpaid && key === this.appConfig.Shared.NAVIGATION.SHOP))
        .map((key: string) => {
          const config = getNavButtonConfig()[key];
          if (!config) return null;
          const genderIcons = GENDER_ICONS[gender]?.[key];
          return genderIcons ? { ...config, ...genderIcons } : config;
        })
        .filter(Boolean),
    );
  }

  private updateActiveButtonFromUrl(url: string): void {
    const [path, queryString] = url.split('?');
    const params = new URLSearchParams(queryString || '');
    const tab = params.get('tab');

    this.isRegimenPage.set(tab === this.appConfig.Shared.TABS.REGIMEN);

    const match = NAV_ROUTE_ENTRIES.find(([, route]) =>
      route.queryParams
        ? Object.entries(route.queryParams).every(([k, v]) => params.get(k) === v)
        : path.startsWith(route.path[0]),
    );

    this.activeButtonKey.set(match?.[0] || this.appConfig.Shared.NAVIGATION.HOME);
  }
}
